import { useCallback, useContext, useMemo } from "react";
import { toast } from "react-toastify";
import { produce } from "immer";
import { store } from "../components/Store";
import { localStorageFavorites, UserAccountFavorites } from "../util/favorites-manager";



/**
 * Hook que gestiona los favoritos del usuario.
 * @returns Un objeto con los siguientes métodos:
 *   - add: Agrega un libro a los favoritos.
 *   - remove: Elimina un libro de los favoritos.
 *   - removeAll: Elimina todos los libros de los favoritos.
 *   - favoriteBooks: Un array con los libros favoritos.
 *   - isFavorite: Un booleano que indica si un libro es favorito o no.
 */
export const useFavoritesManager = () => {
	const appStore = useContext(store);
	const { state, dispatch } = appStore;
	const username = state?.username;
	const userBooks = state?.userBooksData?.books;

	const isPersonalAccount = state?.isPersonalAccount;
	const FavoritesManager = isPersonalAccount
		? UserAccountFavorites.getInstance()
		: localStorageFavorites.getInstance();

	const getLatestFavorites = () => state?.favoriteBooks || [];

	const favoriteIds = useMemo(() => {
		return new Set(state?.favoriteBooks?.map((b) => b.idLibro));
	}, [state?.favoriteBooks]);

	const getBookFromUserBooks = useCallback(
		(bookId) => {
			return userBooks.find((book) => book?.idLibro === bookId)
		},
		[userBooks]
	);

	const add = async ({ bookId }) => {
		const previous = JSON.parse(JSON.stringify(getLatestFavorites()));
		try {
			const book = getBookFromUserBooks(bookId);
			if (!book) throw new Error("Book not found");

			// UI optimista
			dispatch({
				type: "CHANGE",
				path: "favoriteBooks",
				value: produce(previous, (draft) => {
					const exists = draft.some((_book) => _book?.idLibro === bookId);
					if (!exists) draft.push(book);
				}),
			});

			await FavoritesManager.add({ bookId, user: username });
		} catch (error) {
			console.log(error);
			toast.error("Error adding book to favorites");
			// rollback si falla
			dispatch({
				type: "CHANGE",
				path: "favoriteBooks",
				value: [],
			});
		}
	};

	const remove = async ({ bookId }) => {
		const previous = JSON.parse(JSON.stringify(getLatestFavorites()));
		try {
			dispatch({
				type: "CHANGE",
				path: "favoriteBooks",
				value: produce(previous, (draft) => {
					return draft.filter((_book) => _book?.idLibro !== bookId);
				}),
			});
			await FavoritesManager.remove({ bookId, user: username });
		} catch (error) {
			console.log(error);
			toast.error("Error removing book from favorites");
			// rollback si falla
			dispatch({
				type: "CHANGE",
				path: "favoriteBooks",
				value: previous,
			});
		}
	};

	const removeAll = async () => {
		const previous = JSON.parse(JSON.stringify(getLatestFavorites()));

		// UI optimista
		dispatch({
			type: "CHANGE",
			path: "favoriteBooks",
			value: [],
		});

		try {
			await FavoritesManager.removeAll();
		} catch (error) {
			console.log(error);
			toast.error("Error removing all favorites");

			// rollback si falla
			dispatch({
				type: "CHANGE",
				path: "favoriteBooks",
				value: previous,
			});
		}
	};

	const isFavorite = useCallback(({ bookId }) => favoriteIds.has(bookId), [favoriteIds]);

	return {
		add,
		remove,
		removeAll,
		favoriteBooks: getLatestFavorites(),
		isFavorite,
	};
};
